class BankAccountDetailViewController extends BaseTableViewController implements BankInfoLinkCellDelegate, BankInfoAnalyticAccountCellDelegate, BankMovementLinkCellDelegate, BankMovementDifferenceCellDelegate, BankInfoTagsCellDelegate, BankInfoSimilarCellDelegate, SelectLinkEntityDelegate
{
    static newInstance() : BankAccountDetailViewController {
        let vc = new BankAccountDetailViewController("bank-account-detail-view");
        vc.initWithResource("layout/bankaccount/BankAccountDetailView.html")
        return vc;
    }

    private backButton:MUIButton = null;
    private refreshButton:MUIButton = null;
    private calendarButton:MUIButton = null;
    private statusComboBox:MUIComboBox = null;
    private saveButton:MUIButton = null;
    private optionsButton:MUIButton = null;

    private titleLabel:MUILabel = null;
    private statusView:MUIView = null;
    private uncategorizedLabel:MUILabel = null;
    private missingLabel:MUILabel = null;
    private ledgerAccountDropdown:MUIButton = null;
    private totalLabel:MUILabel = null;
    private emptyView:MUIView = null;

    private infoTableView:UITableView = null;
    private infoDatasource:BankEntryInfoDatasource = null;

    private columnFilterController:ColumnFilterController = null;
    private dateFilterTextField:ColumnFilterTextField = null;
    private conceptFilterTextField:ColumnFilterTextField = null;
    private amountFilterTextField:ColumnFilterTextField = null;
    private totalFilterTextField:ColumnFilterTextField = null;

    // "all" or "pending" (the movements without analytic account)
    private statusFilter:string = "all";
    private visibleSections:BankEntry[][] = [];

    // The period shown, shared by every account and bank for the whole session: the current
    // month until another one is picked
    private static periodBegin:Date = null;
    private static periodEnd:Date = null;

    private get beginDateString():string { return MIODateGetDateString(BankAccountDetailViewController.periodBegin) + " 00:00:00"; }
    private get endDateString():string { return MIODateGetDateString(BankAccountDetailViewController.periodEnd) + " 23:59:59"; }

    private periodLabel:MUILabel = null;

    viewDidLoad(){
        super.viewDidLoad();

        this.columnFilterController = new ColumnFilterController();
        this.columnFilterController.initWithDelegate(this);

        if (BankAccountDetailViewController.periodBegin == null || BankAccountDetailViewController.periodEnd == null) {
            let today = MIODateToday();
            BankAccountDetailViewController.periodBegin = new Date(today.getFullYear(), today.getMonth(), 1);
            BankAccountDetailViewController.periodEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        }

        this.periodLabel = MUIOutlet(this, "period-lbl", "MUILabel");
        this.updatePeriod();

        this.backButton = MUIOutlet( this, "back-btn", "MUIButton");
        this.backButton.setAction( this, function(this:BankAccountDetailViewController) {
            this.navigationController.popViewController(true);
        } );

        this.refreshButton = MUIOutlet(this, "refresh-btn", "MUIButton");
        this.refreshButton.setAction(this, function(this:BankAccountDetailViewController){
            this.importMovementsFromAPI(null, null);
        });

        this.calendarButton = MUIOutlet(this, "calendar-btn", "MUIButton");
        this.calendarButton.setAction(this, function(this:BankAccountDetailViewController){
            AppHelper.sharedInstance().presentDateSelectionViewController(null, this);
        });

        // Status is computed from relationships, not stored, so it's filtered in memory
        this.statusComboBox = MUIOutlet(this, "status-cb", "MUIComboBox");
        this.statusComboBox.setOnChangeAction(this, function(this:BankAccountDetailViewController, control:MUIComboBox, value:string){
            this.statusFilter = value;
            this.tableView.reloadData();
        });

        this.saveButton = MUIOutlet(this, "save-btn", "MUIButton");
        this.saveButton.setAction(this, function() {
            DBHelper.saveMainContext();
        })

        this.optionsButton = MUIOutlet(this, "action-btn", "MUIButton");
        this.optionsButton.setAction(this, this.showOptionsAction);

        this.titleLabel = MUIOutlet(this, "account-number-lbl", "MUILabel");
        this.statusView = MUIOutlet(this, "status-view", "MUIView");
        this.uncategorizedLabel = MUIOutlet(this, "uncategorized-lbl", "MUILabel");
        this.missingLabel = MUIOutlet(this, "missing-lbl", "MUILabel");

        this.ledgerAccountDropdown = MUIOutlet(this, "ledger-account-dd", "MUIButton");
        this.ledgerAccountDropdown.setAction(this, function(this:BankAccountDetailViewController){
            AppHelper.sharedInstance().showSelectLedgerAccountViewControllerFromView(this.ledgerAccountDropdown, this._account.ledgerAccount, null, false, this._account.info?.number, this, this.updateLedgerAccount);
        });

        this.totalLabel = MUIOutlet(this, "total-lbl", "MUILabel");
        this.emptyView = MUIOutlet(this, "empty-view", "MUIView");

        this.dateFilterTextField = MUIOutlet(this, "date-hover-filter", "ColumnFilterTextField");
        this.dateFilterTextField.filterController = this.columnFilterController;
        this.dateFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Date, "date", null, null);

        this.conceptFilterTextField = MUIOutlet(this, "concept-hover-filter", "ColumnFilterTextField");
        this.conceptFilterTextField.filterController = this.columnFilterController;
        this.conceptFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "concept", null, null);

        this.amountFilterTextField = MUIOutlet(this, "amount-hover-filter", "ColumnFilterTextField");
        this.amountFilterTextField.filterController = this.columnFilterController;
        this.amountFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Currency, "amount", null, null);

        this.totalFilterTextField = MUIOutlet(this, "total-hover-filter", "ColumnFilterTextField");
        this.totalFilterTextField.filterController = this.columnFilterController;
        this.totalFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Currency, "balance", null, null);

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;

        this.infoTableView = MUIOutlet(this, "info-table-view", "UITableView");
        this.infoDatasource = new BankEntryInfoDatasource();
        this.infoDatasource.initWithTableView( this.infoTableView );
        this.infoDatasource.delegate = this;

        this.noSelectionView = MUIOutlet(this, "no-selection-view", "MUIView");
        this.showEntryDetails(false);
    }

    private noSelectionView:MUIView = null;
    private showEntryDetails(show:boolean){
        if (this.infoTableView != null) this.infoTableView.hidden = !show;
        if (this.noSelectionView != null) this.noSelectionView.hidden = show;
    }

    viewWillAppear(){
        super.viewWillAppear();
        this.updateUI();
        this.reloadSuggestions();
        this.loadAvailableTags();
        document.addEventListener("keydown", this.keyDownHandler);
    }

    viewWillDisappear(animated?:boolean){
        super.viewWillDisappear(animated);
        document.removeEventListener("keydown", this.keyDownHandler);
    }

    private suggester:AnalyticAccountSuggester = null;
    private reloadSuggestions(){
        if (this.suggester == null) {
            this.suggester = new AnalyticAccountSuggester();
            this.suggester.init();
            this.infoDatasource.suggester = this.suggester;
        }

        this.suggester.reload(this, function(this:BankAccountDetailViewController){
            this.reloadSelectedEntry();
        });
    }

    // The chips of the tags section: the ledger account tags of the tags configuration
    private loadAvailableTags(){
        let sd = [MIOSortDescriptor.sortDescriptorWithKey("name", true)];
        let predicate = MIOPredicate.predicateWithFormat("type == " + TagType.LedgerAccount);
        DBHelper.queryObjectsWithCompletion("Tag", sd, predicate, [], this, function(this:BankAccountDetailViewController, objects:Tag[]){
            this.infoDatasource.availableTags = (objects ?? []).map(t => t.name).filter(n => n != null && n.length > 0);
            this.reloadSelectedEntry();
        });
    }

    private _account:BankAccount = null
    set item(item:BankAccount){
        this._account = item;
        this.updateUI();
    }

    private updateUI()
    {
        if (this.viewIsLoaded == false || this._account == null) return;

        let ad = MUIWebApplication.sharedInstance().delegate;

        this.titleLabel.text = this._account.info?.number;
        this.updateAccountStatus();
        this.ledgerAccountDropdown.title = this._account.ledgerAccountName;
        this.totalLabel.text = ad.currencyFormatter.stringFromNumber(this._account.balance);

        this.fetchedResultsController = null;
        this.tableView.reloadData();

        // Changing account clears the selection, back to the empty state
        this.selectedItem = null;
        this.showEntryDetails(false);
    }

    //
    // The status filter is computed in memory (categorized or not), so the table renders
    // from visibleSections: the fetched sections with the status filter applied.
    //

    numberOfSections(tableView:UITableView){
        this.buildVisibleSections();
        return this.visibleSections.length;
    }

    numberOfRowsInSection(tableView:UITableView, section:number){
        return this.visibleSections[section].length;
    }

    private buildVisibleSections(){
        this.visibleSections = [];

        let sections = this.fetchedResultsController.sections;
        for (let index = 0; index < sections.length; index++){
            let rows = [];
            let objects = sections[index].objects;
            for (let row = 0; row < objects.length; row++){
                let item = objects[row] as BankEntry;
                if (this.statusFilter == "pending" && ((item instanceof BankMovement) == false || BankMovementState.isCategorized(item))) continue;
                rows.push(item);
            }
            if (rows.length > 0) this.visibleSections.push(rows);
        }

        if (this.emptyView != null) this.emptyView.hidden = this.visibleSections.length > 0;
    }

    private itemAtIndexPath(indexPath:MIOIndexPath):BankEntry {
        return this.visibleSections[indexPath.section][indexPath.row];
    }

    private indexPathOfItem(item:BankEntry):MIOIndexPath {
        for (let section = 0; section < this.visibleSections.length; section++){
            let row = this.visibleSections[section].indexOf(item);
            if (row > -1) return MIOIndexPath.indexForRowInSection(row, section);
        }
        return null;
    }

    private visibleItems():BankEntry[] {
        let items = [];
        for (let section of this.visibleSections) items = items.concat(section);
        return items;
    }

    titleForHeaderInSection(tableView:UITableView, section:number) {
        let item = this.visibleSections[section][0];

        let yy = MIODateGetYearFromDate ( item.date );
        let mm = MIODateGetStringForMonth ( MIODateGetMonthFromDate( item.date ) );

        return mm + " " + yy;
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        let cell:any = null;
        let item = this.itemAtIndexPath(indexPath);

        if (item instanceof BankMovement) {
            cell = tableView.dequeueReusableCellWithIdentifier('BankMovementCell') as BankMovementCell;
        }
        else if (item instanceof BankOpening) {
            cell = tableView.dequeueReusableCellWithIdentifier('OpeningCell') as BankEntryCell;
        }

        cell.item = item;
        cell.selected = item == this.selectedItem;

        return cell;
    }

    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        this.selectEntry(this.itemAtIndexPath(indexPath), false);
    }

    private selectEntry(entry:BankEntry, scroll:boolean){
        this.selectedItem = entry;
        this.infoDatasource.setEntry(entry, this.similarMovementsFor(entry));
        this.showEntryDetails(entry != null);

        if (scroll == false) return;

        // Keyboard or panel selection: move the highlight and keep the row in view
        this.tableView.reloadData();
        let indexPath = this.indexPathOfItem(entry);
        let cell = indexPath != null ? this.tableView.cellAtIndexPath(indexPath) as UITableViewCell : null;
        if (cell != null) cell.layer.scrollIntoView({ block: "nearest" });
    }

    // After a change on the selected movement: its row, its panel and the banks' counts
    private reloadSelectedEntry(){
        this.tableView.reloadData();
        if (this.selectedItem != null) this.infoDatasource.setEntry(this.selectedItem, this.similarMovementsFor(this.selectedItem));
    }

    // The pending counts of the account and of its bank live in a balance view on the server: once
    // the change has reached it, the account's row comes again and the banks list is told
    private movementDidChange(){
        this.reloadSelectedEntry();

        let account = this._account;
        DBHelper.saveMainContextWithCompletion(this, function(this:BankAccountDetailViewController){
            DBHelper.queryObjectWithCompletion("BankAccount", null, MIOPredicate.predicateWithFormat("identifier == " + account.identifier), ["movementBalance"], this, function(this:BankAccountDetailViewController){
                if (this._account === account) this.updateAccountStatus();
                MIONotificationCenter.defaultCenter().postNotification(BankListViewController.statusDidChangeNotification, null);
            });
        });
    }

    private updateAccountStatus(){
        let status = this._account?.movementBalance;
        let uncategorized = status?.uncategorizedCount ?? 0;
        let missing = status?.missingDocumentsCount ?? 0;
        this.uncategorizedLabel.text = String(uncategorized);
        this.missingLabel.text = String(missing);
        this.uncategorizedLabel.hidden = uncategorized == 0;
        this.missingLabel.hidden = missing == 0;
        this.statusView.hidden = uncategorized == 0 && missing == 0;
    }

    private similarMovementsFor(entry:BankEntry):BankMovement[] {
        if (this.suggester == null || (entry instanceof BankMovement) == false) return [];
        return this.suggester.similarCandidates(entry as BankMovement, this.fetchedResultsController.fetchedObjects);
    }

    //
    // Keyboard, when the focus is not in a field: up and down move the selection, enter jumps to
    // the next uncategorized movement
    //

    private keyDownHandler = (event:KeyboardEvent) => {
        let target = event.target as HTMLElement;
        let tag = target?.tagName;
        if (tag == "INPUT" || tag == "TEXTAREA" || tag == "SELECT" || target?.isContentEditable) return;
        if (this.presentedViewController != null) return;

        if (event.key == "ArrowDown") { event.preventDefault(); this.moveSelection(1); }
        else if (event.key == "ArrowUp") { event.preventDefault(); this.moveSelection(-1); }
        else if (event.key == "Enter") { event.preventDefault(); this.selectNextUncategorized(); }
    }

    private moveSelection(delta:number){
        let items = this.visibleItems();
        if (items.length == 0) return;
        let index = items.indexOf(this.selectedItem);
        let next = index < 0 ? 0 : Math.max(0, Math.min(items.length - 1, index + delta));
        if (items[next] != this.selectedItem) this.selectEntry(items[next], true);
    }

    private selectNextUncategorized(){
        let items = this.visibleItems();
        let index = items.indexOf(this.selectedItem);
        let ordered = items.slice(index + 1).concat(items.slice(0, Math.max(index, 0)));
        for (let item of ordered) {
            if (item instanceof BankMovement && BankMovementState.isCategorized(item) == false) {
                this.selectEntry(item, true);
                return;
            }
        }
    }

    set fetchedResultsController(value){
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;

        this._fetchedResultsController = value;
    }

    get fetchedResultsController(){
        if (this._fetchedResultsController != null) return this._fetchedResultsController;

        let ad = MUIWebApplication.sharedInstance().delegate;

        let sortDescriptors = [
            MIOSortDescriptor.sortDescriptorWithKey('date', false),
            MIOSortDescriptor.sortDescriptorWithKey('orderIndex', false)
        ];

        let predicateFormat = "bankAccount.identifier == " + this._account.identifier;

        predicateFormat += " AND date >= '" + this.beginDateString + "' AND date <= '" + this.endDateString + "'";

        let filterPredicateFormat = this.columnFilterController.filterPredicateFormat();
        if (filterPredicateFormat != null){
            predicateFormat += " AND (" + filterPredicateFormat + ")";
        }

        let fetchRequest = DBHelper.listFetchRequestWithEntityName("BankEntry", sortDescriptors, predicateFormat);
        fetchRequest.relationshipKeyPathsForPrefetching = ["documentLink", "analyticAccount"];

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, "monthString");
        fetchedResultsController.delegate = this;

        fetchedResultsController.performFetch();

        this._fetchedResultsController = fetchedResultsController;
        return this._fetchedResultsController;
    }

    // Under the title: the date, or both dates of a range
    private updatePeriod(){
        let df = (MUIWebApplication.sharedInstance().delegate as AppDelegate).dateFormatter;
        let begin = df.stringFromDate(BankAccountDetailViewController.periodBegin);
        let end = df.stringFromDate(BankAccountDetailViewController.periodEnd);
        this.periodLabel.text = begin == end ? begin : begin + " – " + end;
    }

    filterPredicateDidChange(controller:ColumnFilterController){
        this.fetchedResultsController = null;
        this.tableView.reloadData();
    }

    datesDidSelect(dateTimeFrom:Date, dateTimeTo:Date){
        BankAccountDetailViewController.periodBegin = dateTimeFrom;
        BankAccountDetailViewController.periodEnd = dateTimeTo;
        this.updatePeriod();

        this.fetchedResultsController = null;
        this.tableView.reloadData();
    }

    //
    // Detail panel
    //

    private _linkingEntry:BankEntry = null;
    linkCellDidTapLink(_cell:BankInfoLinkCell, _entry:BankEntry) {
        if ((_entry instanceof BankMovement) == false) return;
        this._linkingEntry = _entry;
        let vc = SelectLinkEntityViewController.newInstance();
        vc.movement = _entry as BankMovement;
        vc.delegate = this;
        this.presentViewController(vc, true);
    }

    analyticAccountCellDidTapDropdown(cell:BankInfoAnalyticAccountCell, entry:BankEntry) {
        AppHelper.sharedInstance().showSelectAnalyticAccountViewControllerFromView(cell, entry.analyticAccount, this, function(this:BankAccountDetailViewController, controller:SelectEntityViewController, account:AnalyticAccount){
            this.assignAnalyticAccount(entry, account);
        });
    }

    analyticAccountCellDidApplySuggestion(cell:BankInfoAnalyticAccountCell, entry:BankEntry, account:AnalyticAccount) {
        this.assignAnalyticAccount(entry, account);
    }

    private assignAnalyticAccount(entry:BankEntry, account:AnalyticAccount) {
        entry.analyticAccount = account;
        entry.analyticAccountName = account?.title;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.managedObjectContext.save();

        if (account != null && this.suggester != null && entry instanceof BankMovement) this.suggester.learn(entry as BankMovement);
        this.movementDidChange();
    }

    tagsCellDidToggleTag(cell:BankInfoTagsCell, entry:BankEntry, tag:string, on:boolean) {
        let tags = BankMovementState.tagsOf(entry).filter(t => t != tag);
        if (on) tags.push(tag);
        BankMovementState.setTags(entry, tags);

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.managedObjectContext.save();
        this.reloadSelectedEntry();
    }

    similarCellDidSelectMovement(cell:BankInfoSimilarCell, movement:BankMovement) {
        this.selectEntry(movement, true);
    }

    // The movement's analytic account and tags go to all the similar ones
    similarCellDidApply(cell:BankInfoSimilarCell, entry:BankMovement, movements:BankMovement[]) {
        let assignments = [];
        for (let index = 0; index < movements.length; index++) assignments.push({ movement: movements[index], account: entry.analyticAccount });
        this.applyAssignments(assignments, BankMovementState.tagsOf(entry));
    }

    // The old picker with a checkbox per similar movement, to apply the account to some of them
    private searchSimilarMovements() {
        let entry = this.selectedItem as BankEntry;
        if (this.suggester == null || (entry instanceof BankMovement) == false) return;
        let mv = entry as BankMovement;

        if (mv.analyticAccount == null) {
            AppHelper.showInfoMessage(this, MIOLocalizeString("INFO","INFO"), MIOLocalizeString("ASSIGN_AN_ANALYTIC_ACCOUNT_FIRST", "ASSIGN AN ANALYTIC ACCOUNT FIRST"));
            return;
        }

        let candidates = this.similarMovementsFor(mv);
        if (candidates.length == 0) {
            AppHelper.showInfoMessage(this, MIOLocalizeString("INFO","INFO"), MIOLocalizeString("NO_SIMILAR_MOVEMENTS_FOUND", "NO SIMILAR MOVEMENTS FOUND"));
            return;
        }

        let vc = SimilarMovementsViewController.newInstance();
        vc.account = mv.analyticAccount;
        vc.movements = candidates;
        vc.completionBlock = (selected:BankMovement[]) => {
            if (selected.length == 0) return;
            let assignments = [];
            for (let index = 0; index < selected.length; index++) assignments.push({ movement: selected[index], account: mv.analyticAccount });
            this.applyAssignments(assignments, null);
        };

        this.presentViewController(vc, true);
    }

    private applyAssignments(assignments:{ movement:BankMovement, account:AnalyticAccount }[], tags:string[]) {
        for (let index = 0; index < assignments.length; index++){
            let movement = assignments[index].movement;
            movement.analyticAccount = assignments[index].account;
            movement.analyticAccountName = assignments[index].account.title;
            if (tags != null && tags.length > 0) {
                let merged = BankMovementState.tagsOf(movement);
                for (let tag of tags) if (merged.indexOf(tag) < 0) merged.push(tag);
                BankMovementState.setTags(movement, merged);
            }
            this.suggester.learn(movement);
        }

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.managedObjectContext.save();

        this.movementDidChange();
    }

    // Invoices open their attached files in the manager's document viewer
    linkCellDidTapView(cell:BankMovementLinkCell, link:BankMovementDocumentLink) {
        if (BankMovementLinkCell.canViewLinkedEntity(link.linkedEntityName) == false || link.linkedEntityID == null) return;

        DBHelper.queryObjectWithCompletion(link.linkedEntityName, null, MIOPredicate.predicateWithFormat("identifier == " + link.linkedEntityID), [], this, function(this:BankAccountDetailViewController, document:DBCoreDocument){
            if (document == null) {
                AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("DOCUMENT_NOT_FOUND", "Document not found"));
                return;
            }

            let vc = InvoiceFilesViewController.newInstance();
            vc.document = document;
            this.presentViewController(vc, true);
        });
    }

    // MARK: - Journal entry generation

    private generateJournalEntriesForAll() {
        let objects = (this.fetchedResultsController?.fetchedObjects ?? []) as BankEntry[];
        let pending = objects.filter(function(entry:BankEntry){
            return entry instanceof BankMovement
                && BankMovementState.isCategorized(entry)
                && entry.journalEntry == null
                && entry.journalEntryIgnored != true;
        }) as BankMovement[];

        if (pending.length == 0) {
            AppHelper.showInfoMessage(this, MIOLocalizeString("GENERATE_JOURNAL_ENTRIES", "GENERATE JOURNAL ENTRIES"), MIOLocalizeString("NO_PENDING_MOVEMENTS_TO_GENERATE", "NO PENDING MOVEMENTS TO GENERATE"));
            return;
        }

        this.generateNextJournalEntry(pending, 0, 0, []);
    }

    private generateNextJournalEntry(pending:BankMovement[], index:number, generated:number, failed:string[]) {
        if (index >= pending.length) {
            let msg = generated + "/" + pending.length + " " + MIOLocalizeString("JOURNAL_ENTRIES_GENERATED", "JOURNAL ENTRIES GENERATED");
            if (failed.length > 0) msg += ". " + MIOLocalizeString("WITH_ERRORS", "WITH ERRORS") + ": " + failed.join(", ");
            AppHelper.showInfoMessage(this, MIOLocalizeString("GENERATE_JOURNAL_ENTRIES", "GENERATE JOURNAL ENTRIES"), msg);
            this.fetchedResultsController = null;
            this.tableView.reloadData();
            return;
        }

        let mv = pending[index];
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.calculateAccountingJournalEntries("bank-entry", mv.identifier, (json:any, error:string) => {
            let errors = json?.Errors ?? [];
            let ok = error == null && errors.length == 0;
            if (!ok) failed.push(mv.concept ?? mv.identifier);
            this.generateNextJournalEntry(pending, index + 1, generated + (ok ? 1 : 0), failed);
        });
    }

    private suggestAnalyticAccountsForAll() {
        if (this.suggester == null) return;

        let assignments = [];
        let objects = this.fetchedResultsController.fetchedObjects;
        for (let index = 0; index < objects.length; index++){
            let mv = objects[index];
            if ((mv instanceof BankMovement) == false) continue;
            if (BankMovementState.isCategorized(mv)) continue;
            let suggestion = this.suggester.suggestionFor(mv as BankMovement, false); // exact match tiers only
            if (suggestion != null) assignments.push({ movement: mv as BankMovement, account: suggestion.account });
        }

        if (assignments.length == 0) {
            AppHelper.showInfoMessage(this, MIOLocalizeString("INFO","INFO"), MIOLocalizeString("NO_SUGGESTIONS_FOUND", "NO SUGGESTIONS FOUND"));
            return;
        }

        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("APPLY_SUGGESTIONS", "APPLY SUGGESTIONS"), assignments.length + " " + MIOLocalizeString("MOVEMENTS_WILL_BE_CATEGORIZED", "MOVEMENTS WILL BE CATEGORIZED"), MUIAlertViewStyle.Default);

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("APPLY","APPLY"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            this.applyAssignments(assignments, null);
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    linkCandidatesDidSelected(controller:SelectLinkEntityViewController, candidates:LinkEntityCandidate[]){
        if (candidates.length == 0 || this._linkingEntry == null) return;

        let mv = this._linkingEntry as BankMovement;
        this._linkingEntry = null;

        if (candidates.length == 1) {
            // Link with the document's own amount when known: a partial link keeps
            // the difference visible so it can be allocated to a concept
            let amount = (candidates[0].amount ?? 0) != 0 ? candidates[0].amount : mv.amount;
            this.createDocumentLinks(mv, candidates, [amount]);
            return;
        }

        // When the selected documents already add up to the movement amount
        // there is nothing to review, link them with their own amounts
        let amounts = [];
        let total = 0;
        for (let index = 0; index < candidates.length; index++){
            amounts.push(candidates[index].amount ?? 0);
            total += candidates[index].amount ?? 0;
        }

        if (Math.abs(Math.abs(total) - Math.abs(mv.amount)) <= 0.005) {
            this.createDocumentLinks(mv, candidates, amounts);
        }
        else {
            this.showLinkAmountsAlert(mv, candidates);
        }
    }

    private showLinkAmountsAlert(mv:BankMovement, candidates:LinkEntityCandidate[]) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let cf = ad.currencyFormatter;

        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("LINK_DOCUMENTS", "LINK DOCUMENTS"), MIOLocalizeString("FILL_AMOUNT_PER_DOCUMENT", "FILL THE AMOUNT FOR EACH DOCUMENT") + " (" + cf.stringFromNumber(mv.amount) + ")", MUIAlertViewStyle.Default);

        // The text fields are just the editing UI: keep the document amounts as
        // numbers and only parse a field back when the user actually changed it
        let prefillTexts:string[] = [];
        for (let index = 0; index < candidates.length; index++){
            let candidate = candidates[index];
            prefillTexts.push(candidate.amount != null ? cf.stringFromNumber(candidate.amount) : null);
            avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
                textField.setPlaceholderText(candidate.title);
                if (prefillTexts[index] != null) textField.text = prefillTexts[index];
            });
        }

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("LINK","LINK"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            let amounts = [];
            for (let index = 0; index < candidates.length; index++){
                let text = avc.textFields[index].text;
                if (text == prefillTexts[index] && candidates[index].amount != null) amounts.push(candidates[index].amount);
                else amounts.push(cf.numberFromString(text) ?? 0);
            }

            // The amounts don't need to add up to the movement: any difference
            // stays visible in the links panel to be allocated to a concept
            this.createDocumentLinks(mv, candidates, amounts);
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    private createDocumentLinks(mv:BankMovement, candidates:LinkEntityCandidate[], amounts:number[]) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        for (let index = 0; index < candidates.length; index++){
            let link = MIOEntityDescription.insertNewObjectForEntityForName("BankMovementDocumentLink", ad.managedObjectContext) as BankMovementDocumentLink;
            link.identifier = MIOUUID.UUID().UUIDString;
            link.linkedEntityName = candidates[index].entityName;
            link.linkedEntityID = candidates[index].entityID;
            // Display snapshot so the panel does not need to fetch the linked entity
            link.concept = candidates[index].title?.length > 0 ? candidates[index].title : candidates[index].entityName;
            link.amount = amounts[index];
            link.date = candidates[index].date ?? mv.date;
            link.movement = mv;

            // Invoice links carry the client or supplier and the document number,
            // resolved from the invoice itself
            if (candidates[index].entityName == "SalesInvoice" || candidates[index].entityName == "LicenseInvoice" || candidates[index].entityName == "PurchaseInvoice") {
                this.fillLinkInvoiceInfo(link);
            }

            // A transfer links both movements together, create the inverse link on the counterpart
            if (candidates[index].entityName == "BankMovement") {
                this.createInverseTransferLink(mv, candidates[index].entityID, amounts[index]);
            }
        }

        ad.managedObjectContext.save();
        this.movementDidChange();
    }

    private fillLinkInvoiceInfo(link:BankMovementDocumentLink) {
        DBHelper.queryObjectWithCompletion(link.linkedEntityName, null, MIOPredicate.predicateWithFormat("identifier == " + link.linkedEntityID), ["legalEntity"], this, function(this:BankAccountDetailViewController, invoice:Invoice){
            if (invoice == null) return;

            link.documentNumber = invoice.documentNumber;
            if (invoice.legalEntity != null) {
                link.legalEntity = invoice.legalEntity;
                link.legalEntityName = invoice.legalEntity.name;
            }

            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            ad.managedObjectContext.save();
        });
    }

    // MARK: - Difference allocation to concepts

    differenceCellDidTapAdd(cell:BankMovementDifferenceCell, entry:BankEntry, remainingAmount:number) {
        if ((entry instanceof BankMovement) == false) return;
        let mv = entry as BankMovement;

        AppHelper.sharedInstance().showSelectBankMovementConceptViewControllerFromView(cell, null, this, function(this:BankAccountDetailViewController, controller:SelectEntityViewController, concept:BankMovementConcept){
            if (concept == null) return;
            this.showConceptAmountAlert(mv, concept, remainingAmount);
        });
    }

    private showConceptAmountAlert(mv:BankMovement, concept:BankMovementConcept, remainingAmount:number) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let cf = ad.currencyFormatter;

        let avc = new MUIAlertViewController();
        avc.initWithTitle(concept.name, MIOLocalizeString("FILL_THE_DIFFERENCE_AMOUNT", "FILL THE DIFFERENCE AMOUNT"), MUIAlertViewStyle.Default);

        // Keep the remainder as a number, only parse the field if the user edited it
        let prefillText = cf.stringFromNumber(remainingAmount);
        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("AMOUNT","AMOUNT"));
            textField.text = prefillText;
        });

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ADD","ADD"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            let text = avc.textFields[0].text;
            let amount = text == prefillText ? remainingAmount : cf.numberFromString(text);
            if (amount == null || amount == 0) return;
            this.createConceptLink(mv, concept, amount);
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    private createConceptLink(mv:BankMovement, concept:BankMovementConcept, amount:number) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let link = MIOEntityDescription.insertNewObjectForEntityForName("BankMovementDocumentLink", ad.managedObjectContext) as BankMovementDocumentLink;
        link.identifier = MIOUUID.UUID().UUIDString;
        link.linkedEntityName = "BankMovementConcept";
        link.linkedEntityID = concept.identifier;
        link.concept = concept.name;
        link.amount = amount;
        link.date = mv.date;
        link.movement = mv;

        ad.managedObjectContext.save();
        this.movementDidChange();
    }

    linkCellDidTapUnlink(cell:BankMovementLinkCell, link:BankMovementDocumentLink) {
        let mv = link.movement;

        // Transfers are linked on both sides, remove the inverse link too
        if (link.linkedEntityName == "BankMovement" && mv != null) {
            this.removeInverseTransferLink(link.linkedEntityID, mv.identifier);
        }

        link.movement = null;
        DBHelper.deleteObjectFromMainContext(link, true);

        this.movementDidChange();
    }

    private createInverseTransferLink(mv:BankMovement, counterpartID:string, amount:number) {
        DBHelper.queryObjectWithCompletion("BankMovement", null, MIOPredicate.predicateWithFormat("identifier == " + counterpartID), ["documentLink"], this, function(this:BankAccountDetailViewController, counterpart:BankMovement){
            if (counterpart == null) return;

            // Already linked back, nothing to do
            let links = counterpart.documentLink?.allObjects ?? [];
            for (let index = 0; index < links.length; index++){
                if ((links[index] as BankMovementDocumentLink).linkedEntityID == mv.identifier) return;
            }

            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            let link = MIOEntityDescription.insertNewObjectForEntityForName("BankMovementDocumentLink", ad.managedObjectContext) as BankMovementDocumentLink;
            link.identifier = MIOUUID.UUID().UUIDString;
            link.linkedEntityName = "BankMovement";
            link.linkedEntityID = mv.identifier;
            link.concept = ad.dateFormatter.stringFromDate(mv.date) + " · " + mv.concept;
            link.amount = -amount;
            link.date = mv.date;
            link.movement = counterpart;
            ad.managedObjectContext.save();
        });
    }

    private removeInverseTransferLink(counterpartID:string, movementID:string) {
        DBHelper.queryObjectsWithCompletion("BankMovementDocumentLink", null, MIOPredicate.predicateWithFormat("movement.identifier == " + counterpartID + " AND linkedEntityID == " + movementID), [], this, function(this:BankAccountDetailViewController, objects:BankMovementDocumentLink[]){
            for (let index = 0; index < objects.length; index++){
                DBHelper.deleteObjectFromMainContext(objects[index], true);
            }
        });
    }

    private showOptionsAction(){
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("ACTIONS","ACTIONS"), MIOLocalizeString("CHOOSE_AN_ACTION", "CHOOSE AN ACTION"), MUIAlertViewStyle.Default);
 
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("SEND_PAYMENT_LINK", "SEND PAYMENT LINK"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            this.askForEmail();
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("MAKE_TRANSFER", "MAKE TRANSFER"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            this.askForBankAccount();
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ADD_OPEN_MOVEMENT", "ADD_OPEN_MOVEMENT"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            this.showOpeningMovementAlert();
        }));

        if (this.selectedItem instanceof BankMovement) {
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("SEARCH_SIMILAR", "SEARCH SIMILAR..."), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
                this.searchSimilarMovements();
            }));
        }

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("SUGGEST_ANALYTIC_ACCOUNTS", "SUGGEST ANALYTIC ACCOUNTS"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            this.suggestAnalyticAccountsForAll();
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("GENERATE_JOURNAL_ENTRIES", "GENERATE JOURNAL ENTRIES"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            this.generateJournalEntriesForAll();
        }));

        // avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("MOVE_TO_ANOTHER_ACCOUNT", "MOVE TO ANOTHER ACCOUNT"), MUIAlertActionStyle.Default, this, function(){
        // }));

        if (this._account.apiReference != null) {
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("IMPORT_FROM_API", "IMPORT FROM API"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
                this.showImportDatesPicker();
            }));

            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("UPDATE_BALANCE_FROM_API", "UPDATE BALANCE FROM API"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
                this.updateBalanceFromAPI();
            }));

        }

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("IMPORT_FROM_EXCEL", "IMPORT FROM EXCEL"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            this.importFromXLSX();
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("IMPORT_NORMA_43", "IMPORT NORMA 43"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            this.importFromNorma43(); 
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }
    
    private askForEmail(){
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("INFO","INFO"), MIOLocalizeString("FILL_THE_REQUIRED_INFO", "FILL THE REQUIRED INFO"), MUIAlertViewStyle.Default);

        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("EMAIL","EMAIL"));
        });

        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("NAME","NAME"));
        });
        
        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("AMOUNT","AMOUNT"));
        });

        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("CONCEPT","CONCEPT"));
        });

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("SEND","SEND"), MUIAlertActionStyle.Default, this, function(){
            let email = avc.textFields[0].text;
            let name = avc.textFields[1].text;
            let amount = avc.textFields[2].text;
            let concept = avc.textFields[3].text;

            this.sendPaymentLink(email, name, parseFloat(amount), concept);            
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    private askForBankAccount(){
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("INFO","INFO"), MIOLocalizeString("FILL_THE_REQUIRED_INFO", "FILL THE REQUIRED INFO"), MUIAlertViewStyle.Default);

        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("IBAN","IBAN"));
        });
        
        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("AMOUNT","AMOUNT"));
        });

        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("CONCEPT","CONCEPT"));
        });

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("SEND","SEND"), MUIAlertActionStyle.Default, this, function(){
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }    

    private sendPaymentLink(email:string, name:string, amount, concept:string){

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.sendPaymentLink(email, name, amount, concept, null, this, function (code, data){
            if (code == 200) {
                AppHelper.showInfoMessage(this, MIOLocalizeString("INFO","INFO"), MIOLocalizeString("PAYMENT_LINK_SENT", "PAYMENT LINK SENT"));
            }
            else {
                AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR","ERROR"), MIOLocalizeString("ERROR_SENDING_EMAIL_PLEASE_TRY_AGAIN_LATER", "ERROR SENDING EMAIL. PLEASE TRY AGAIN LATER"));
            }
        });        
    }

    private showImportDatesPicker() {
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("INFO","INFO"), MIOLocalizeString("FILL_THE_REQUIRED_INFO", "FILL THE REQUIRED INFO"), MUIAlertViewStyle.Default);

        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("BEGIN_DATE", "BEGIN DATE"));
        });
        
        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("END_DATE", "END DATE"));
        });

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("IMPORT","IMPORT"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            let begin_date_str = avc.textFields[0].text;
            let begin_date = (MUIWebApplication.sharedInstance().delegate as AppDelegate).dateFormatter.dateFromString( begin_date_str );

            let end_date_str = avc.textFields[1].text;
            let end_date = (MUIWebApplication.sharedInstance().delegate as AppDelegate).dateFormatter.dateFromString( end_date_str );

            this.importMovementsFromAPI(begin_date, end_date);
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);        
    }

    private showOpeningMovementAlert() {
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("INFO","INFO"), MIOLocalizeString("FILL_THE_REQUIRED_INFO", "FILL THE REQUIRED INFO"), MUIAlertViewStyle.Default);

        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("DATE", "DATE"));
        });
        
        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("AMOUNT", "AMOUNT"));
        });

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ADD","ADD"), MUIAlertActionStyle.Default, this, function(this:BankAccountDetailViewController){
            let date_str = avc.textFields[0].text;
            let date = (MUIWebApplication.sharedInstance().delegate as AppDelegate).dateFormatter.dateFromString( date_str );

            let amount = (MUIWebApplication.sharedInstance().delegate as AppDelegate).numberFormatter.numberFromString( avc.textFields[1].text );
            this.addOpeningMovement(date, amount);
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);        
    }

    private addOpeningMovement(date:Date, amount:number) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let mv = MIOEntityDescription.insertNewObjectForEntityForName("BankOpening", ad.managedObjectContext) as BankOpening;
        mv.bankAccount = this._account;
        mv.date = date;
        mv.amount = amount;
        mv.balance = amount;
        mv.concept = "Opening movement";
        mv.orderIndex = 0;

        ad.managedObjectContext.save();
    }

    private importMovementsFromAPI(beginDate:Date, endDate:Date) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.importMovementsFromAPI(this._account, beginDate, endDate, (result:boolean) => {
            this.fetchedResultsController = null;
            this.tableView.reloadData();
        });
    }

    private updateBalanceFromAPI() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.updateBalanceFromAPI(this._account, (result:boolean) => {
            this.fetchedResultsController = null;
            this.tableView.reloadData();
        });
    }

    private importFromXLSX(){
        let panel = MUIOpenPanel.openPanel();
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        panel.beginSheetModalForWindow(ad.window, this, function(this:BankAccountDetailViewController, result:number){
            if (result == MIOFileHandlingPanel.OKButton) {
               let file = panel.files[0];
               // Open the document.
               this.parseXLSX(file);
            }       
         });
    }

    private parseXLSX(file){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        // NOTE: importBankMovements is declared with 4 params (file, account, type, completion) in WebService+Bank.ts
        // but implemented with 3 (file, account, completion); this call matches the implementation, so the declaration is stale.
        (ad.webService as any).importBankMovements(file, this._account, (result:boolean) => {
            let sortDescriptors = [
                MIOSortDescriptor.sortDescriptorWithKey('date', false),
                MIOSortDescriptor.sortDescriptorWithKey('orderIndex', false)
            ];
        
            let pf = "deletedAt = null && bankAccount.identifier = " + this._account.identifier;
            DBHelper.queryObjectWithCompletion( "BankEntry", sortDescriptors, MIOPredicate.predicateWithFormat(pf), ["bankAccount"], this, function(this:BankAccountDetailViewController){
                this.reloadData();
            } );
        });
    }


    private importFromNorma43(){
        let panel = MUIOpenPanel.openPanel();
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        panel.beginSheetModalForWindow(ad.window, this, function(this:BankAccountDetailViewController, result:number){
            if (result == MIOFileHandlingPanel.OKButton) {
               let file = panel.files[0];
               // Open the document.
               this.parseNorma43(file);
            }       
         });
    }

    private parseNorma43(file:any){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.importBankMovements(file, this._account, "norma43", (result:boolean) => {
            MIOLog("BANK KK");
        });
    }

    private updateLedgerAccount(controller:SelectViewController, account:LedgerAccount){
        this._account.ledgerAccount = account;
        this._account.ledgerAccountName = account?.description;
        this.ledgerAccountDropdown.title = account?.description;
    }

}